import {
  EmoConfetti,
  EmoGift,
  EmoLocation,
  EmoQuest,
  EmoTarget,
  EmoTrophy,
  Open,
} from "@assets"
import { Link, router } from "@inertiajs/react"
import { Calendar, MessageCircle, Store } from "lucide-react"
import { useState } from "react"

import { WeeklyChallengeModal } from "@/components/feed/weekly-challenge-modal"
import { Button } from "@/components/ui/button"
import { CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { feedPath } from "@/routes"

interface PersonalizedStats {
  events_this_weekend: number
  active_raffles: number
  unanswered_questions: number
}

interface Activity {
  type: "event_rsvp" | "new_business" | "forum_activity"
  text: string
  timestamp: string
  url?: string
}

interface MissedActivityStats {
  new_events_in_neighborhood: number
  answers_on_your_question: number
  raffles_ended: number
}

interface WeeklyChallengeData {
  challenges: {
    key: string
    title: string
    points: number
    icon: string
    completed: boolean
  }[]
  days_remaining: number
  all_completed: boolean
  total_possible_points: number
}

interface PersonalizedCardsProps {
  stats: PersonalizedStats
  userName?: string
  activities?: Activity[]
  neighborhoodName?: string
  daysSinceLastVisit?: number
  missed?: MissedActivityStats
  weeklyChallenges?: WeeklyChallengeData
  userPoints?: number
}

export function PersonalizedCards({
  stats,
  activities,
  neighborhoodName,
  daysSinceLastVisit,
  missed,
  weeklyChallenges,
  userPoints,
}: PersonalizedCardsProps) {
  const showMissedCard =
    typeof daysSinceLastVisit === "number" && daysSinceLastVisit >= 3
  const cards = [
    {
      key: "eventos",
      icon: EmoConfetti,
      count: stats.events_this_weekend,
      label: "EVENTOS ESTE FINDE",
      color: "text-primary",
    },
    {
      key: "sorteos",
      icon: EmoGift,
      count: stats.active_raffles,
      label: stats.active_raffles === 1 ? "SORTEO ACTIVO" : "SORTEOS ACTIVOS",
      color: "text-primary",
    },
    {
      key: "foro",
      icon: EmoQuest,
      count: stats.unanswered_questions,
      label: "POR RESPONDER",
      color: "text-secondary",
    },
  ]

  const handleCardClick = (filterKey: string) => {
    router.get(
      feedPath(),
      {
        filter: filterKey,
      },
      {
        preserveState: true,
        preserveScroll: false,
      },
    )
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case "event_rsvp":
        return <Calendar className="text-primary h-4 w-4" />
      case "new_business":
        return <Store className="text-active h-4 w-4" />
      case "forum_activity":
        return <MessageCircle className="text-star h-4 w-4" />
      default:
        return null
    }
  }
  const completedCount =
    weeklyChallenges?.challenges.filter((c) => c.completed).length ?? 0
  const totalCount = weeklyChallenges?.challenges.length ?? 0
  const [showModal, setShowModal] = useState(false)
  return (
    <div className="bg-background">
      {weeklyChallenges && weeklyChallenges.challenges.length > 0 && (
        <div className="bg-card mt-[12px] flex items-center gap-3 px-[9px] pt-[6px] pb-[7px]">
          <span className="bg-modal flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-full p-0.5 text-4xl">
            <img
              src={completedCount === totalCount ? EmoTrophy : EmoTarget}
              alt="Challenge"
              className="h-full w-full"
            />
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-start justify-between font-semibold">
              <div className="text-chart-5 h-auto min-w-0 flex-1 pr-2 text-[14px] leading-[21px] tracking-[0px] text-wrap break-words">
                Desafíos de la Semana
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="text-chart-3 flex h-[21px] flex-shrink-0 items-center gap-1 text-[12px] leading-[18px] tracking-[0.18px] hover:underline"
              >
                Ver
                <Open className="fill-chart-3 h-[12px] w-[12px]" />
              </button>
            </div>
            <div className="bg-modal h-[8px] overflow-hidden rounded-[204px]">
              <div
                className={`h-full rounded-[204px] transition-all ${completedCount === totalCount ? "bg-active" : "bg-star"}`}
                style={{ width: `${(completedCount / totalCount) * 100}%` }}
              />
            </div>
          </div>

          <span
            className={`h-[39px] w-[52px] flex-shrink-0 text-center text-[24px] leading-[35px] font-bold tracking-[0px] ${completedCount === totalCount ? "text-active" : "text-star"}`}
          >
            {completedCount}/{totalCount}
          </span>
        </div>
      )}
      <div className="gradient-feed border-chart-4 flex w-full grid-cols-3 items-center justify-center gap-[12px] border-b px-[12.5px] pt-[15px] pb-[12px]">
        {cards.map((card) => (
          <div
            key={card.key}
            onClick={() => handleCardClick(card.key)}
            className="flex w-full flex-col items-center justify-center text-center"
          >
            <div className="flex h-[39px] w-full items-center justify-center gap-4 rounded-[6px] bg-[#EEF2FF] px-[9px] py-[5px] text-center dark:bg-[#2A2A2A]">
              <img
                src={card.icon}
                alt=""
                className={
                  card.icon === EmoConfetti
                    ? "-ml-1 h-[42px] w-[36px]"
                    : card.icon === EmoGift
                      ? "-ml-1 h-[38px] w-[32px]"
                      : card.icon === EmoQuest
                        ? "-ml-1 h-[35px] w-[35px]"
                        : "h-full w-auto"
                }
              />
              <div
                className={`h-[29px] flex-1 text-[21px] font-bold ${card.color}`}
              >
                {card.count}
              </div>
            </div>
            <div className="text-card-foreground h-[12px] pt-[3px] text-center text-[9px] leading-[10px] font-bold tracking-[0.27px]">
              {card.label}
            </div>
          </div>
        ))}
      </div>

      {showMissedCard ? (
        <div className="mt-2">
          <CardHeader className="pb-2 md:px-0">
            <CardTitle className="text-base font-semibold">
              👋 ¡Volviste! Te extrañamos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {missed &&
            (missed.new_events_in_neighborhood > 0 ||
              missed.answers_on_your_question > 0 ||
              missed.raffles_ended > 0) ? (
              <>
                <div className="text-muted-foreground text-sm">
                  Mientras no estabas:
                </div>
                <div className="text-foreground space-y-1 text-sm">
                  {missed.new_events_in_neighborhood > 0 && (
                    <div>
                      • {missed.new_events_in_neighborhood} evento
                      {missed.new_events_in_neighborhood === 1 ? "" : "s"} nuevo
                      {missed.new_events_in_neighborhood === 1 ? "" : "s"} en tu
                      barrio
                    </div>
                  )}
                  {missed.answers_on_your_question > 0 && (
                    <div>
                      • Tu pregunta tiene {missed.answers_on_your_question}{" "}
                      respuesta
                      {missed.answers_on_your_question === 1 ? "" : "s"}
                    </div>
                  )}
                  {missed.raffles_ended > 0 && (
                    <div>
                      • {missed.raffles_ended} sorteo
                      {missed.raffles_ended === 1 ? "" : "s"} terminaron
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="text-muted-foreground text-sm">
                ¡Qué bueno que volviste! Te extrañamos en la comunidad.
              </div>
            )}
            <div className="flex w-full justify-center">
              <Button
                variant="ghost"
                className="text-primary hover:bg-muted"
                onClick={() => router.visit("/mi-barrio")}
              >
                Ponerse al día
              </Button>
            </div>
          </CardContent>
        </div>
      ) : (
        activities &&
        activities.length > 0 && (
          <div className="mt-2">
            <CardHeader className="flex items-center pb-1 font-semibold md:px-0">
              <span className="bg-background flex h-[24px] w-[24px] flex-shrink-0 items-center justify-center rounded-full text-white">
                <img src={EmoLocation} className="" />
              </span>
              {`En ${neighborhoodName}`}
            </CardHeader>
            <CardContent className="pt-0">
              {activities.map((activity, index) => (
                <div key={index} className="flex items-start gap-2 pl-1">
                  <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                  {activity.url ? (
                    <Link
                      href={activity.url}
                      prefetch="hover"
                      className="text-foreground flex-1 text-sm hover:underline"
                    >
                      {activity.text}
                    </Link>
                  ) : (
                    <p className="text-foreground flex-1 text-sm">
                      {activity.text}
                    </p>
                  )}
                </div>
              ))}
            </CardContent>
            <div className="flex w-full justify-center">
              <Button
                variant="ghost"
                size="sm"
                className="text-primary hover:bg-muted"
                onClick={() => router.visit("/mi-barrio")}
              >
                Ver todo
              </Button>
            </div>
          </div>
        )
      )}

      {/* Modal de Desafíos Semanales */}
      {weeklyChallenges && (
        <WeeklyChallengeModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          challenges={weeklyChallenges.challenges}
          allCompleted={weeklyChallenges.all_completed}
          totalPossiblePoints={weeklyChallenges.total_possible_points}
          userTotalPoints={userPoints}
        />
      )}
    </div>
  )
}
